import { apiJson } from "./queryClient";
import {
  shouldOfferSetupGuide,
  type SetupGuideStatus,
  type SetupGuideOfferInput,
} from "@client/lib/setupGuideKey";

export type { SetupGuideStatus, SetupGuideOfferInput } from "@client/lib/setupGuideKey";
// Geteilte Entscheidungslogik mit der mobilen App (single source of truth).
export { shouldOfferSetupGuide };

export const SETUP_GUIDE_KEY_PREFIX = "preflight:setupGuideStatus";

/**
 * Der Onboarding-Status wird pro Konto (User-ID) getrennt abgelegt, damit auf
 * einem geteilten Browser/Gerät der Status nicht zwischen Konten durchsickert.
 * Ohne User-ID wird der Basis-Schlüssel verwendet; dieser unterdrückt den
 * Assistenten NICHT für ein später angemeldetes Konto, da der Lesepfad dann den
 * konto-spezifischen Schlüssel abfragt.
 */
export function setupGuideKey(userId?: string | null): string {
  return userId ? `${SETUP_GUIDE_KEY_PREFIX}:${userId}` : SETUP_GUIDE_KEY_PREFIX;
}

export function isValidSetupGuideStatus(value: unknown): value is SetupGuideStatus {
  return value === "completed" || value === "skipped";
}

/**
 * Konto-gebundener Status + inhaltsbasierte Heuristik vom Server. Quelle der
 * Wahrheit für „schon erledigt/übersprungen" sowie „Konto hat bereits Daten".
 */
export interface ServerSetupGuideState {
  status: SetupGuideStatus | null;
  hasContent: boolean;
}

export async function fetchSetupGuideState(): Promise<ServerSetupGuideState> {
  const data = await apiJson<{ status?: unknown; hasContent?: unknown }>(
    "GET",
    "/api/setup-guide/state",
  );
  return {
    status: isValidSetupGuideStatus(data?.status) ? data.status : null,
    hasContent: data?.hasContent === true,
  };
}

/**
 * Persistiert den Status konto-gebunden auf dem Server. Best-effort: Fehler
 * werden geloggt, aber nicht eskaliert (der lokale Status greift als Fallback).
 */
export async function persistServerSetupGuideStatus(
  status: SetupGuideStatus,
): Promise<void> {
  try {
    await apiJson("POST", "/api/setup-guide/status", { status });
  } catch (e) {
    console.warn("[setupGuide] Server-Status konnte nicht gespeichert werden:", e);
  }
}

export function getSetupGuideStatus(userId?: string | null): SetupGuideStatus | null {
  try {
    const value = localStorage.getItem(setupGuideKey(userId));
    return isValidSetupGuideStatus(value) ? value : null;
  } catch {
    return null;
  }
}

export function setSetupGuideStatus(
  status: SetupGuideStatus,
  userId?: string | null,
): void {
  try {
    localStorage.setItem(setupGuideKey(userId), status);
  } catch {
    // Best-effort — Onboarding-Status ist nicht kritisch.
  }
}
